import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Eraser, RefreshCw, Sprout } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API, ax, errorText } from "./common";
import TestRunPanel from "./TestRunPanel";

// Platform administrator only: scans for data named as test data and removes it after a confirmation.
export default function TestDataTab({ showMsg, refreshTick = 0 }) {
  const { t } = useLang();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/test-data`, ax);
      setScan(data);
    } catch (e) { if (!silent) showMsg("error", errorText(e, t("adm.error"))); }
    setLoading(false);
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(true); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const clean = async () => {
    setConfirmOpen(false);
    try {
      const { data } = await axios.post(`${API}/admin/test-data`, { confirm: scan.confirm }, ax);
      showMsg("success", t("adm.td.done", { n: data.total }));
    } catch (e) {
      showMsg("error", e?.response?.status === 409 ? t("adm.td.changed") : errorText(e, t("adm.error")));
    }
    load();
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-data/seed`, {}, ax);
      setSeeded(data);
      showMsg("success", t("adm.td.seeded", { n: data.users }));
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    setSeeding(false);
    load();
  };

  const rows = scan ? Object.entries(scan.counts).filter(([, n]) => n > 0) : [];

  return (
    <div className="space-y-3">
      <TestRunPanel showMsg={showMsg} />
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-2">
        <p className="font-semibold text-slate-900 text-sm">{t("adm.td.title")}</p>
        <p className="text-sm text-slate-600">{t("adm.td.intro")}</p>
        <ul className="text-xs text-slate-500 font-mono space-y-0.5">
          <li>{t("adm.td.rule_tenant")}: dene-test-tenant-*</li>
          <li>{t("adm.td.rule_warehouse")}: DENE-TEST-DEPO-*</li>
          <li>{t("adm.td.rule_email")}: *@dene-test.dom</li>
          <li>{t("adm.td.rule_asin")}: TEST_*</li>
        </ul>
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => load()} disabled={loading} className="gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> {t("adm.td.scan")}
        </Button>
        {scan?.seed_enabled && (
          <Button size="sm" onClick={seed} disabled={seeding || scan.total > 0} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
            data-testid="seed-test-data-button">
            <Sprout className="w-4 h-4" /> {t("adm.td.seed")}
          </Button>
        )}
        <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={!scan || scan.total === 0}
          className="gap-1.5 bg-red-600 hover:bg-red-700">
          <Eraser className="w-4 h-4" /> {t("adm.td.delete")}
        </Button>
      </div>

      {seeded && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2" data-testid="seed-result">
          <p className="text-sm font-semibold text-emerald-900">
            {t("adm.td.seed_result", { password: seeded.password })}
          </p>
          <p className="text-xs text-emerald-800">
            {t("adm.td.seed_counts", { tenants: seeded.tenants, warehouses: seeded.warehouses, users: seeded.users,
              products: seeded.products, stores: seeded.stores, fnsku: seeded.fnsku })}
          </p>
          <div className="divide-y divide-emerald-100 bg-white rounded-xl border border-emerald-100 text-xs">
            {seeded.accounts.map(a => (
              <div key={a.email} className="px-3 py-1.5 flex flex-wrap gap-x-3">
                <span className="font-mono text-slate-900">{a.email}</span>
                <span className="text-slate-500">{a.role}</span>
                <span className="text-slate-400">{a.tenant || a.warehouse}</span>
                {a.capabilities.length > 0 && <span className="text-slate-400">{a.capabilities.join(", ")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-50">
        {scan && rows.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">{t("adm.td.empty")}</p>}
        {rows.map(([name, n]) => (
          <div key={name} className="flex items-center justify-between px-4 py-2 text-sm">
            <span className="font-mono text-slate-700">{name}</span>
            <span className="font-semibold text-slate-900">{n}</span>
          </div>
        ))}
        {scan && rows.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 text-sm bg-slate-50 rounded-b-2xl">
            <span className="font-semibold text-slate-700">{t("adm.td.total")}</span>
            <span className="font-extrabold text-slate-900">{scan.total}</span>
          </div>
        )}
      </div>

      {scan && scan.storage_file_count > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          {t("adm.td.files_note", { n: scan.storage_file_count })}
        </p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("adm.td.confirm_title", { n: scan?.total ?? 0 })}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">{t("adm.td.confirm_body")}</p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={clean}>
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
